import styles from './ResendCountdown.module.css';

// 16px ring, 2px stroke, so the stroke's centre line sits on a radius of 7.
const SIZE = 16;
const STROKE = 2;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The resend wait, drawn: a ring that drains as the seconds run out, beside the sentence that says them.
 *
 * Greyed controls with nothing moving read as a step that is stuck. The ring is the part that moves, so a
 * reader can see the wait has an end before they read how far off it is. Both phones draw the same ring
 * and the same line (`ResendCountdownView` on iOS, `ResendCountdown` on Android).
 *
 * The ring empties CLOCKWISE from twelve o'clock, like a clock hand sweeping, on all three surfaces.
 *
 * `left` is the fraction of the wait still to run (see `fractionLeft`). It is re-read on every tick of the
 * caller's clock, and the arc eases between ticks over the same 250ms, so the drain looks continuous
 * without a second clock. Reduced motion drops the easing and leaves the steps.
 *
 * No `aria-live`: a number that changes every second would be re-announced every second. The controls it
 * explains point at it with `aria-describedby`, which reads it once, on focus.
 */
export function ResendCountdown({
  id,
  label,
  left,
  className,
}: {
  id?: string;
  label: string;
  left: number;
  /** Spacing from the caller. The countdown sets none of its own, so no two stylesheets compete for it. */
  className?: string;
}) {
  // NEGATIVE, so the gap opens at twelve o'clock and grows clockwise: the elapsed part is eaten the way
  // a clock hand sweeps, and the arc that remains always ends at twelve. A positive offset drains the other
  // way (the free end retreats counter-clockwise), which reads as time running backwards.
  const offset = -CIRCUMFERENCE * (1 - Math.min(1, Math.max(0, left)));
  return (
    <p id={id} className={className ? `${styles.root} ${className}` : styles.root} data-testid="resend-countdown">
      <svg
        className={styles.ring}
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        aria-hidden
        focusable="false"
      >
        <circle className={styles.track} cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} strokeWidth={STROKE} />
        <circle
          className={styles.arc}
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          strokeWidth={STROKE}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
        />
      </svg>
      <span>{label}</span>
    </p>
  );
}
